import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Command, Search, ArrowRight, Upload, Music, Music2, Music4, Library, Sliders,
  Drum, Sparkles, AudioLines, Disc3, Share2, Compass, BookOpenText, Mic2,
  Download, Save, Headphones, Play, Square, Repeat, Timer, Users, ShieldCheck, Activity,
} from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";

/* ─────────────────────────────────────────────────────────────────────────
 *  Command Palette — Cmd/Ctrl + K
 *
 *  Inspired by VS Code / Linear / Raycast: a searchable, keyboard-navigable
 *  surface that exposes every studio action in one place. Each command has:
 *   - id           Stable identifier (for "recents" persistence).
 *   - label        Primary text shown in the result row.
 *   - keywords     Comma-separated synonyms (matched but not displayed).
 *   - section      Grouping label (Open · Transport · Toggles · Share).
 *   - icon         lucide-react component.
 *   - run()        Side-effect to perform on Enter / click.
 *
 *  Match algorithm: case-insensitive substring on `label + keywords`. Empty
 *  query returns recents-first, then everything else in section order. We
 *  intentionally do NOT pull in a fuzzy-search lib — substring is plenty
 *  for ~25 commands and keeps the bundle flat.
 *
 *  Recents: top 5 most-recently-run ids persist in localStorage["elpm-cmd-recents"].
 *
 *  Keyboard:
 *   - ↑/↓        Move highlight  (wraps; visual indicator on the highlighted row)
 *   - Enter      Run highlighted command (closes palette)
 *   - Esc        Close
 *   - Tab        Move highlight one row down (mouse-free flow)
 *  ───────────────────────────────────────────────────────────────────────── */

const RECENTS_KEY = "elpm-cmd-recents";
const MAX_RECENTS = 5;

function loadRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_RECENTS) : [];
  } catch (e) { void e; return []; }
}

function pushRecent(id) {
  try {
    const curr = loadRecents().filter((x) => x !== id);
    const next = [id, ...curr].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (e) { void e; }
}

/** Highlight any character span of `text` that matches `query` (case-insensitive
 *  contiguous match). Returns a fragment with the matched span wrapped in <mark>. */
function highlight(text, query) {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-fuchsia-400/30 px-[1px] text-fuchsia-100">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function CommandPalette({ open, onClose, actions }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [recents, setRecents] = useState(() => loadRecents());

  useEscapeClose(open, onClose);

  // Re-reset state every time the palette opens. Querying for "sun" → close
  // → reopen should start fresh.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setRecents(loadRecents());
      // requestAnimationFrame ensures the input exists post-mount.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Whole catalog. Built dynamically because each command's `run` closes over
  // the parent's open/close setters via the `actions` prop bag. Memoize so
  // the array reference stays stable across renders of the parent (avoids
  // re-running the filter useMemo every render).
  const commands = useMemo(() => {
    const all = [
      // ───── Open · ──────────────────────────────────────────────
      { id: "open-suno",         section: "Open",      label: "Make Music",        hint: "AI music generation",        keywords: "ai song music suno generate",     icon: Music4,        run: actions.openSuno },
      { id: "open-song",         section: "Open",      label: "Make a Song",       hint: "Build a song from scratch",  keywords: "song create build",               icon: Music,         run: actions.openSong },
      { id: "open-lyrics",       section: "Open",      label: "Write Lyrics",      hint: "ChatGPT-powered lyrics writer", keywords: "lyrics write chatgpt gpt ai words",  icon: BookOpenText,  run: actions.openLyrics },
      { id: "open-voice",        section: "Open",      label: "Voice Clone",       hint: "ElevenLabs voice cloning",   keywords: "voice clone elevenlabs tts ai", icon: Mic2,          run: actions.openVoice },
      { id: "open-drum",         section: "Open",      label: "Drum Machine",      hint: "16-step drum sequencer",     keywords: "drum machine beat kick snare",     icon: Drum,          run: actions.openDrum },
      { id: "open-bass",         section: "Open",      label: "Bass Player",       hint: "Bass-line generator",        keywords: "bass player synth low end",        icon: Music2,        run: actions.openBass },
      { id: "open-loops",        section: "Open",      label: "Loops",             hint: "Loop library",               keywords: "loops library samples",           icon: AudioLines,    run: actions.openLoops },
      { id: "open-jam",          section: "Open",      label: "Jam Mode",          hint: "One-click full jam track",   keywords: "jam wizard instant",              icon: Sparkles,      run: actions.openJam },
      { id: "open-editor",       section: "Open",      label: "Audio Editor",      hint: "Multi-track waveform editor", keywords: "editor multi-track waveform",    icon: AudioLines,    run: actions.openMtEditor },
      { id: "open-library",      section: "Open",      label: "My Songs",          hint: "Saved + AI-generated library", keywords: "library songs saved my",        icon: Library,       run: actions.openLibrary },
      { id: "open-recordings",   section: "Open",      label: "Recordings",        hint: "Disk-streamed recording sessions", keywords: "recordings sessions disk", icon: Disc3,         run: actions.openRecordings },
      { id: "open-midi",         section: "Open",      label: "MIDI Bridge",       hint: "Hardware control surface",    keywords: "midi controller hardware mackie", icon: Sliders,       run: actions.openMidi },
      { id: "open-midi-clip",    section: "Open",      label: "MIDI Clip Editor",  hint: "Pianoroll · compose a melody",  keywords: "midi clip pianoroll roll melody piano notes compose write", icon: Music4, run: actions.openMidiClip },
      { id: "open-arrangement",  section: "Open",      label: "Arrangement",       hint: "Multi-track timeline · drag clips", keywords: "arrangement timeline arrange clips lanes daw multi-track", icon: AudioLines, run: actions.openArrangement },
      { id: "open-explore",      section: "Open",      label: "Explore",           hint: "Public sessions gallery",     keywords: "explore discover gallery public", icon: Compass,       run: actions.openExplore },
      { id: "open-admin",        section: "Open",      label: "Admin Dashboard",   hint: "Storage + janitor + system health", keywords: "admin dashboard storage janitor cleanup health ops operator", icon: ShieldCheck, run: actions.openAdmin },
      { id: "open-status",       section: "Open",      label: "Studio Status",     hint: "Self-test every collab primitive", keywords: "status health probe self-test diagnostic debug troubleshoot", icon: Activity, run: actions.openStatus },

      // ───── Transport · ─────────────────────────────────────────
      { id: "toggle-play",       section: "Transport", label: "Play / Pause",      hint: "Toggle playback",            keywords: "play pause space",                icon: Play,          run: actions.togglePlay },
      { id: "stop",              section: "Transport", label: "Stop",              hint: "Stop & rewind playhead",     keywords: "stop reset rewind",               icon: Square,        run: actions.stop },
      { id: "toggle-loop",       section: "Transport", label: "Toggle Loop",       hint: "Loop the session on playback", keywords: "loop wrap repeat",              icon: Repeat,        run: actions.toggleLoop },

      // ───── Toggles · ───────────────────────────────────────────
      { id: "toggle-metronome",  section: "Toggles",   label: "Toggle Metronome",  hint: "Click track on/off",         keywords: "metronome click beat",            icon: Timer,         run: actions.toggleMetronome },
      { id: "toggle-dry-mode",   section: "Toggles",   label: "Toggle Dry Mode",   hint: "Bypass reverb + delay",      keywords: "dry mode bypass wet",             icon: Sparkles,      run: actions.toggleDryMode },

      // ───── Files · ─────────────────────────────────────────────
      { id: "upload",            section: "Files",     label: "Upload Audio",      hint: "Load a file into a channel", keywords: "upload audio file mp3 wav",       icon: Upload,        run: actions.openUpload },
      { id: "save-session",      section: "Files",     label: "Save Session",      hint: "Snapshot to local storage",  keywords: "save persist snapshot session",   icon: Save,          run: actions.saveSession },
      { id: "load-session",      section: "Files",     label: "Load Session",      hint: "Restore last saved session", keywords: "load restore session reload",     icon: Library,       run: actions.loadSession },
      { id: "export-wav",        section: "Files",     label: "Export Master WAV", hint: "Bounce the master mix",      keywords: "export bounce wav master",        icon: Download,      run: actions.exportWav },

      // ───── Share · ─────────────────────────────────────────────
      { id: "share-session",     section: "Share",     label: "Share Session",     hint: "Publish as a public page",   keywords: "share publish public link",       icon: Share2,        run: actions.openShare },
      { id: "open-collab",       section: "Share",     label: "Collab Room",       hint: "Real-time co-mixing",        keywords: "collab collaborate live room peers websocket", icon: Users, run: actions.openCollab },
    ];
    // Drop commands whose handler wasn't wired (defensive — parent may omit
    // some actions during partial integration).
    return all.filter((c) => typeof c.run === "function");
  }, [actions]);

  /** Apply the current search query. Empty query → recents first, then every
   *  command (de-duped). Non-empty → substring match on label + keywords. */
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const byId = new Map(commands.map((c) => [c.id, c]));
      const recentCmds = recents.map((id) => byId.get(id)).filter(Boolean);
      const remaining = commands.filter((c) => !recents.includes(c.id));
      // Add a synthetic "recent" section flag onto recents so the renderer
      // can group them at the top without changing the underlying record.
      return [
        ...recentCmds.map((c) => ({ ...c, section: "Recent" })),
        ...remaining,
      ];
    }
    return commands.filter((c) =>
      c.label.toLowerCase().includes(q) ||
      c.keywords.toLowerCase().includes(q) ||
      (c.hint || "").toLowerCase().includes(q),
    );
  }, [commands, query, recents]);

  // Clamp activeIdx to the result count any time the result set changes.
  useEffect(() => { setActiveIdx(0); }, [query, results.length]);

  // Group results into sections for rendering. Maintains the order in which
  // sections first appear in `results` (so Recent comes first when present).
  const sections = useMemo(() => {
    const groups = [];
    const seen = new Map();
    results.forEach((c, i) => {
      const key = c.section || "Other";
      if (!seen.has(key)) {
        seen.set(key, groups.length);
        groups.push({ name: key, items: [] });
      }
      groups[seen.get(key)].items.push({ ...c, flatIdx: i });
    });
    return groups;
  }, [results]);

  const runCommand = useCallback((cmd) => {
    if (!cmd?.run) return;
    pushRecent(cmd.id);
    // Close BEFORE running so the palette doesn't stay open under the modal
    // that the command opens (which would create a stacking-order mess).
    onClose?.();
    // setTimeout(0) so the close animation can start before the new modal
    // mounts — keeps the framer-motion exit animation clean.
    setTimeout(() => { try { cmd.run(); } catch (e) { console.warn("[cmd]", e); } }, 0);
  }, [onClose]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey)) {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = results[activeIdx];
      if (cmd) runCommand(cmd);
    }
  };

  // Auto-scroll the active row into view (keeps it visible during arrow-nav).
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="commandpalette-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] grid place-items-start bg-black/70 px-4 pt-[12vh] backdrop-blur-md"
          onClick={onClose}
          data-testid="command-palette-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[640px] overflow-hidden rounded-3xl border border-white/10 bg-[#0b0e1c] text-white shadow-2xl"
          >
            {/* Search row */}
            <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
              <Search className="h-4 w-4 text-white/45" />
              <input
                ref={inputRef}
                data-testid="command-palette-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type a command or search…"
                className="flex-1 bg-transparent font-body text-[15px] text-white placeholder:text-white/35 focus:outline-none"
              />
              <kbd className="hidden rounded-md border border-white/10 bg-white/[0.05] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/55 sm:inline-block">
                Esc
              </kbd>
            </div>

            {/* Results */}
            <div
              ref={listRef}
              data-testid="command-palette-results"
              className="max-h-[60vh] overflow-y-auto py-2"
            >
              {results.length === 0 ? (
                <div
                  data-testid="command-palette-empty"
                  className="px-4 py-8 text-center"
                >
                  <p className="font-display text-[14px] text-white/65">No matching commands</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/35">
                    Try “suno”, “share”, “drum”, “loop”…
                  </p>
                </div>
              ) : (
                sections.map((section) => (
                  <div key={`sect-${section.name}`}>
                    <div className="px-4 pb-1 pt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">
                      {section.name}
                    </div>
                    {section.items.map((cmd) => {
                      const Icon = cmd.icon;
                      const isActive = cmd.flatIdx === activeIdx;
                      return (
                        <button
                          key={cmd.id}
                          type="button"
                          data-testid={`command-palette-item-${cmd.id}`}
                          data-cmd-idx={cmd.flatIdx}
                          onMouseEnter={() => setActiveIdx(cmd.flatIdx)}
                          onClick={() => runCommand(cmd)}
                          className={`flex w-full items-center gap-3 px-4 py-2 text-left transition ${
                            isActive
                              ? "bg-fuchsia-500/15 text-white"
                              : "text-white/80 hover:bg-white/[0.04]"
                          }`}
                        >
                          <span className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg ${
                            isActive
                              ? "bg-fuchsia-500/30 text-fuchsia-100 ring-1 ring-fuchsia-400/40"
                              : "bg-white/[0.05] text-white/65"
                          }`}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="font-display text-[13.5px] font-medium leading-none">
                              {highlight(cmd.label, query)}
                            </span>
                            <span className="hidden font-body text-[11.5px] leading-none text-white/45 sm:inline">
                              · {cmd.hint}
                            </span>
                          </span>
                          {isActive && (
                            <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-fuchsia-200" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer hint row */}
            <div className="flex items-center justify-between gap-2 border-t border-white/10 bg-black/30 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white/45">
              <span className="flex items-center gap-2">
                <Command className="h-3 w-3" />Command palette
              </span>
              <span className="flex items-center gap-2.5">
                <span className="flex items-center gap-1">
                  <kbd className="rounded border border-white/10 bg-white/[0.05] px-1.5 py-0.5">↑</kbd>
                  <kbd className="rounded border border-white/10 bg-white/[0.05] px-1.5 py-0.5">↓</kbd>
                  to navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="rounded border border-white/10 bg-white/[0.05] px-1.5 py-0.5">↵</kbd>
                  to run
                </span>
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Tiny header button — a compact ⌘K affordance you can drop into the status
 * strip. Doesn't open the palette directly; it just calls the supplied
 * onOpen handler so the parent stays the source of truth. */
export function CommandPaletteHeaderButton({ onOpen }) {
  const isMac = typeof navigator !== "undefined" && /Mac|iPad|iPhone|iPod/.test(navigator.platform);
  const shortcut = isMac ? "⌘K" : "Ctrl K";
  return (
    <button
      type="button"
      data-testid="command-palette-trigger-btn"
      onClick={onOpen}
      title="Command Palette · search every studio action"
      className="studio-pill flex items-center gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/60 transition hover:border-white/20 hover:bg-white/[0.05] hover:text-white/95"
    >
      <Search className="h-3.5 w-3.5" />
      <span>Quick · Search</span>
      <kbd className="rounded border border-white/10 bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] text-white/55">
        {shortcut}
      </kbd>
    </button>
  );
}
